import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  LogOut, Search, RefreshCw, Trash2, Phone, Mail, MapPin, Calendar, Sparkles,
  Loader2, Package, Clock, CheckCircle2, XCircle, Cog, Truck, Home,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { BRAND } from '../data/mock';
import AdminTabs from '../components/AdminTabs';

const STATUS_META = {
  pending:       { label: 'Pending',       cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  confirmed:     { label: 'Confirmed',     cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_production: { label: 'In Production', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  delivered:     { label: 'Delivered',     cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  cancelled:     { label: 'Cancelled',     cls: 'bg-stone-200 text-stone-600 border-stone-300' },
};

const StatCard = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-white rounded-2xl border border-pink-100 shadow-sm p-4 flex items-center gap-3" data-testid={`orders-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <div className="text-2xl font-display font-bold text-stone-900 leading-none">{value}</div>
      <div className="text-xs text-stone-500 mt-1">{label}</div>
    </div>
  </div>
);

const AdminOrders = () => {
  const { user, loading, logout, authAxios } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, in_production: 0, delivered: 0, cancelled: 0, total_coconuts: 0 });
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: list }, { data: st }] = await Promise.all([
        authAxios.get('/admin/orders', { params: { status: statusFilter, q: q || undefined } }),
        authAxios.get('/admin/orders/stats'),
      ]);
      setOrders(list);
      setStats(st);
    } finally {
      setBusy(false);
    }
  }, [authAxios, statusFilter, q]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  const selected = orders.find((o) => o.id === selectedId) || null;

  const updateOrder = async (id, patch) => {
    const { data } = await authAxios.patch(`/admin/orders/${id}`, patch);
    setOrders((list) => list.map((o) => (o.id === id ? data : o)));
    if (selected?.id === id) setNotesDraft(data.admin_notes || '');
    const { data: st } = await authAxios.get('/admin/orders/stats');
    setStats(st);
  };

  const removeOrder = async (id) => {
    if (!window.confirm('Delete this order permanently?')) return;
    await authAxios.delete(`/admin/orders/${id}`);
    setOrders((list) => list.filter((o) => o.id !== id));
    if (selected?.id === id) setSelectedId(null);
    const { data: st } = await authAxios.get('/admin/orders/stats');
    setStats(st);
  };

  const openDetail = (o) => {
    setSelectedId(o.id);
    setNotesDraft(o.admin_notes || '');
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="bg-white border-b border-pink-100 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src={BRAND.logo} alt="logo" className="w-10 h-10 rounded-full object-contain" />
            <div className="leading-tight">
              <div className="font-script text-xs text-pink-brand -mb-0.5">Simply Original</div>
              <div className="font-display font-bold text-stone-900">Mr. Coconetto · Admin</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs text-stone-500">{user.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-full border-pink-200 text-pink-brand hover:bg-blush/60"
              data-testid="orders-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <AdminTabs />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-8">
          <StatCard icon={Package} label="Total" value={stats.total} tint="bg-blush text-pink-brand" />
          <StatCard icon={Clock} label="Pending" value={stats.pending} tint="bg-amber-100 text-amber-700" />
          <StatCard icon={CheckCircle2} label="Confirmed" value={stats.confirmed} tint="bg-blue-100 text-blue-700" />
          <StatCard icon={Cog} label="Production" value={stats.in_production} tint="bg-purple-100 text-purple-700" />
          <StatCard icon={Truck} label="Delivered" value={stats.delivered} tint="bg-emerald-100 text-emerald-700" />
          <StatCard icon={XCircle} label="Cancelled" value={stats.cancelled} tint="bg-stone-200 text-stone-600" />
          <StatCard icon={Sparkles} label="Coconuts" value={stats.total_coconuts} tint="bg-pink-100 text-pink-brand" />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-pink-100 shadow-sm p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search name, phone, city, order no, event…"
              className="pl-9 rounded-full"
              data-testid="orders-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[200px] rounded-full" data-testid="orders-status-filter-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="orders-status-filter-all">All statuses</SelectItem>
              {Object.entries(STATUS_META).map(([k, m]) => (
                <SelectItem key={k} value={k} data-testid={`orders-status-filter-${k}`}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-full"
            disabled={busy}
            data-testid="orders-refresh-button"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {/* Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3" data-testid="orders-list">
            {busy && orders.length === 0 ? (
              <div className="p-10 text-center text-stone-500 bg-white rounded-2xl border border-pink-100">
                <Loader2 className="w-6 h-6 mx-auto animate-spin text-pink-brand" />
                <div className="mt-2 text-sm">Loading orders…</div>
              </div>
            ) : orders.length === 0 ? (
              <div className="p-14 text-center bg-white rounded-2xl border border-dashed border-pink-200" data-testid="orders-empty-state">
                <div className="w-16 h-16 rounded-full bg-blush/60 flex items-center justify-center mx-auto mb-3">
                  <Package className="w-7 h-7 text-pink-brand" />
                </div>
                <div className="font-display font-semibold text-stone-800">No orders yet</div>
                <div className="text-sm text-stone-500 mt-1">
                  When someone customizes their coconut and hits Submit, it'll appear here.
                </div>
              </div>
            ) : (
              orders.map((o) => {
                const meta = STATUS_META[o.status] || STATUS_META.pending;
                const active = o.id === selectedId;
                return (
                  <button
                    type="button"
                    key={o.id}
                    onClick={() => openDetail(o)}
                    className={`w-full text-left bg-white rounded-2xl border p-4 transition-all shadow-sm hover:shadow-md ${
                      active ? 'border-pink-300 ring-2 ring-pink-100' : 'border-pink-100'
                    }`}
                    data-testid={`orders-row-${o.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-[11px] font-semibold text-pink-brand bg-blush/60 rounded-full px-2 py-0.5">{o.order_no}</span>
                          <span className="font-display font-bold text-stone-900">{o.name}</span>
                          <Badge className={`${meta.cls} border rounded-full text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5`}>
                            {meta.label}
                          </Badge>
                        </div>
                        <div className="text-sm text-stone-700 mt-1 truncate">
                          <span className="font-script text-pink-brand text-base">"</span>
                          <span className="font-semibold">{o.coconut_text}</span>
                          <span className="font-script text-pink-brand text-base">"</span>
                          {' · '}<span className="text-stone-500">{o.engraving_style}</span>
                        </div>
                        <div className="text-xs text-stone-500 mt-1 flex items-center gap-3 flex-wrap">
                          <span className="inline-flex items-center gap-1"><Sparkles className="w-3.5 h-3.5 text-pink-brand" /> {o.event_name}</span>
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-pink-brand" /> {o.city}</span>
                          <span className="inline-flex items-center gap-1"><Package className="w-3.5 h-3.5" /> {o.pieces} pcs</span>
                          {o.event_date && <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {o.event_date}</span>}
                        </div>
                      </div>
                      <div className="text-[11px] text-stone-400 whitespace-nowrap">
                        {new Date(o.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Detail panel */}
          <aside className="lg:sticky lg:top-24 h-fit" data-testid="orders-detail-panel">
            {!selected ? (
              <div className="bg-white rounded-2xl border border-dashed border-pink-200 p-8 text-center text-sm text-stone-500">
                Select an order to view details and update status.
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-pink-100 shadow-sm p-5 space-y-4">
                <div>
                  <div className="text-xs text-stone-500 font-mono">{selected.order_no}</div>
                  <div className="font-display font-bold text-xl text-stone-900">{selected.name}</div>
                  <div className="text-xs text-stone-500 mt-0.5">Placed {new Date(selected.created_at).toLocaleString('en-IN')}</div>
                </div>

                <div className="rounded-2xl bg-blush/60 border border-pink-100 p-3 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-pink-brand">To be engraved</div>
                  <div className="mt-1 font-display font-bold text-xl text-stone-900 break-words">"{selected.coconut_text}"</div>
                  <div className="text-[11px] text-stone-500 mt-1 uppercase tracking-wide">{selected.engraving_style} · {selected.pieces} pcs</div>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <a href={`tel:${selected.phone}`} className="rounded-xl border border-pink-200 bg-blush/60 px-3 py-2 text-pink-brand hover:bg-blush inline-flex items-center gap-2" data-testid="orders-detail-call-link">
                    <Phone className="w-3.5 h-3.5" /> Call
                  </a>
                  <a href={`https://wa.me/${selected.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="rounded-xl border border-pink-200 bg-blush/60 px-3 py-2 text-pink-brand hover:bg-blush inline-flex items-center gap-2" data-testid="orders-detail-whatsapp-link">
                    <Sparkles className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>

                <div className="grid gap-1.5 text-sm text-stone-700">
                  <div><span className="text-stone-500">Phone:</span> {selected.phone}</div>
                  {selected.email && <div><span className="text-stone-500">Email:</span> {selected.email}</div>}
                  <div><span className="text-stone-500">Event:</span> {selected.event_name}</div>
                  {selected.event_date && <div><span className="text-stone-500">Date:</span> {selected.event_date}</div>}
                  <div><span className="text-stone-500">City:</span> {selected.city}</div>
                  <div className="mt-1 rounded-lg bg-stone-50 border border-stone-200 p-2">
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1"><Home className="w-3 h-3" /> Delivery Address</div>
                    <div className="mt-1 text-stone-700 whitespace-pre-wrap">{selected.address}</div>
                  </div>
                  {selected.notes && (
                    <div className="mt-1 rounded-lg bg-blush/40 border border-pink-100 p-2 text-stone-700 whitespace-pre-wrap">
                      <div className="text-[10px] uppercase tracking-widest text-pink-brand">Special Requests</div>
                      {selected.notes}
                    </div>
                  )}
                </div>

                <div>
                  <div className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1">Status</div>
                  <Select
                    value={selected.status}
                    onValueChange={(v) => updateOrder(selected.id, { status: v })}
                  >
                    <SelectTrigger data-testid="orders-detail-status-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_META).map(([k, m]) => (
                        <SelectItem key={k} value={k} data-testid={`orders-detail-status-option-${k}`}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <div className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-1">Internal Notes</div>
                  <Textarea
                    rows={3}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder="Production notes, payment status, dispatch info…"
                    data-testid="orders-detail-notes-input"
                  />
                  <div className="mt-2 flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => updateOrder(selected.id, { admin_notes: notesDraft })}
                      className="rounded-full bg-pink-brand hover:bg-[#BE185D] text-white"
                      data-testid="orders-detail-save-notes-button"
                    >
                      Save notes
                    </Button>
                  </div>
                </div>

                <div className="pt-3 border-t border-pink-100 flex justify-between items-center">
                  <span className="text-[11px] text-stone-400">ID {selected.id.slice(0, 8)}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOrder(selected.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-full"
                    data-testid="orders-detail-delete-button"
                  >
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default AdminOrders;
